import React from 'react';
import { Table } from 'antd';
import { Pencil, Mail, Users } from 'lucide-react';
import { TagsDisplay } from '../../tags/elements';
import { IconAction, IconActions, StatusTag, EmptyState } from '../../common';
import { useUserTags } from './usersTable.controller';
import { t } from '../../../company/labels';
import { isUserTypeEnabled } from '../../../company/session';

const ROLE_TONES = {
    gerente: 'brand',
    asesor: 'warning',
    promotor: 'violet',
    auditor: 'info',
    analista: 'brand',
    auditAnalyst: 'warning',
    admin: 'danger',
    client: 'success'
};

const getRoleTone = (role) => ROLE_TONES[role] || 'neutral';

const getRoleLabel = (role) => {
    const labels = {
        gerente: 'Gerente',
        asesor: 'Asesor',
        promotor: t('promotor'),
        auditor: 'Auditor',
        analista: `Supervisor ${t('promotor')}`,
        admin: 'Admin',
        location: 'Ubicación',
        client: 'Cliente',
        route: 'Ruta',
        TradeMarketing: 'Trade Marketing',
        auditAnalyst: 'Análisis Auditor'
    };
    return labels[role] || role;
};

/** Two-letter initials for the avatar chip. */
const initials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase();

export default function UsersTable({ rows, loading, showEditDrawer }) {
    const { availableTags, hasTag } = useUserTags(rows);

    const usersColumns = [
        {
            title: 'Usuario',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <div className="flex items-center gap-3 min-w-0">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-600">
                        {initials(name) || '?'}
                    </span>
                    <div className="min-w-0">
                        <span className="block truncate text-sm font-medium text-surface-900">{name}</span>
                        <span className="block text-xs text-surface-500 tabular-nums">{record.rif || 'Sin RIF'}</span>
                    </div>
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
        },
        {
            title: 'Correo',
            dataIndex: 'email',
            key: 'email',
            render: (email) => (
                <a className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors" href={`mailto:${email}`}>
                    <Mail className="text-surface-400" />
                    {email}
                </a>
            )
        },
        {
            title: 'Rol',
            dataIndex: 'userType',
            key: 'userType',
            render: (role) => <StatusTag tone={getRoleTone(role)}>{getRoleLabel(role)}</StatusTag>,
            filters: [
                { text: 'Gerente', value: 'gerente' },
                { text: 'Asesor', value: 'asesor' },
                { text: t('promotor'), value: 'promotor' },
                { text: 'Auditor', value: 'auditor' },
                { text: `Supervisor ${t('promotor')}`, value: 'analista' },
                { text: 'Ruta', value: 'route' },
                { text: 'Trade Marketing', value: 'TradeMarketing' },
                { text: 'Análisis Auditor', value: 'auditAnalyst' },
                { text: 'Ubicación', value: 'location' },
                { text: 'Admin', value: 'admin' },
                { text: 'Cliente', value: 'client' }
            ].filter((option) => isUserTypeEnabled(option.value)),
            onFilter: (value, record) => record.userType === value
        },
        {
            title: 'Canal',
            dataIndex: 'channel',
            key: 'channel',
            render: (channel) => <StatusTag tone={channel === 'moderno' ? 'info' : 'warning'}>{channel === 'moderno' ? 'Moderno' : 'Tradicional'}</StatusTag>,
            filters: [
                { text: 'Moderno', value: 'moderno' },
                { text: 'Tradicional', value: 'tradicional' }
            ],
            onFilter: (value, record) => record.channel === value
        },
        {
            title: 'Estado',
            dataIndex: 'active',
            key: 'active',
            render: (active) => (
                <StatusTag tone={active ? 'success' : 'danger'} dot>
                    {active ? 'Activo' : 'Inactivo'}
                </StatusTag>
            )
        },
        {
            title: 'Etiquetas',
            dataIndex: '_id',
            key: 'tags',
            width: 200,
            render: (id) => <TagsDisplay modelType="User" modelId={id} editable={true} />,
            filters: availableTags.map((tag) => ({
                text: tag.name,
                value: tag._id
            })),
            filterMultiple: true,
            onFilter: (value, record) => hasTag(record._id, value)
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 90,
            render: (_, record) => (
                <IconActions>
                    <IconAction icon={<Pencil />} label="Editar" onClick={() => showEditDrawer(record)} />
                </IconActions>
            )
        }
    ];

    return (
        <Table
            loading={loading}
            dataSource={rows}
            columns={usersColumns}
            pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} usuarios`
            }}
            rowKey="_id"
            size="middle"
            scroll={{ x: 1000 }}
            locale={{ emptyText: <EmptyState icon={<Users />} title="No hay usuarios" description="Prueba con otra búsqueda o cambia el filtro de estado." /> }}
        />
    );
}
